import React, { useEffect, useState } from "react";
import { Btn, Card, Field, Overline } from "@/components/kit";
import { api, apiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { formatDateGB } from "@/lib/me";

export default function Admin() {
  const [rows, setRows] = useState(null);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/authorized-emails");
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/api/admin/authorized-emails", { email: email.trim() });
      toast.success(data.message);
      setEmail("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAdding(false);
    }
  };

  const setStatus = async (row, action) => {
    try {
      await api.post(`/api/admin/authorized-emails/${encodeURIComponent(row.email)}/${action}`);
      toast.success(action === "revoke" ? "Access revoked." : "Access re-activated.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const invite = async (row) => {
    try {
      const { data } = await api.post(`/api/admin/authorized-emails/${encodeURIComponent(row.email)}/invite`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="max-w-3xl mx-auto em-fade-up" data-testid="admin-page">
      <Overline>Owner · Access</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-2">Authorised clients</h1>
      <p className="text-inksoft mt-3 mb-8 leading-relaxed">
        Only the email addresses below may create an account. Journal entries are private to each client
        and are never accessible here.
      </p>

      <Card className="mb-8">
        <form onSubmit={add} className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <Field label="Authorise a new email" type="email" placeholder="client@example.com"
            value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1" data-testid="admin-add-email" />
          <Btn type="submit" disabled={adding} data-testid="admin-add-btn">{adding ? "Adding…" : "Add access"}</Btn>
        </form>
      </Card>

      {rows === null ? (
        <div className="text-inkfaint text-sm animate-pulse">Loading…</div>
      ) : (
        <div className="space-y-2" data-testid="admin-list">
          {rows.map((r) => (
            <div key={r.email} className="bg-panel border border-line rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <div className="text-ink">{r.email} {r.is_owner && <span className="text-gold text-xs">· owner</span>}</div>
                <div className="text-xs text-faint mt-0.5">
                  Added {r.created_at ? formatDateGB(r.created_at) : "—"} ·{" "}
                  {r.activated ? "Account activated" : r.invited ? "Invited — not yet registered" : "Not yet registered"}
                  {r.last_login_at ? ` · last login ${formatDateGB(r.last_login_at)}` : ""}
                </div>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full ${r.status === "active" ? "bg-golddark text-gold" : "bg-panel2 text-faint"}`}>
                {r.status}
              </span>
              {!r.is_owner && r.status === "active" && !r.activated && (
                <button onClick={() => invite(r)} className="text-gold text-sm hover:underline" data-testid={`invite-${r.email}`}>
                  {r.invited ? "Resend invite" : "Send invite"}
                </button>
              )}
              {!r.is_owner && (r.status === "active" ? (
                <button onClick={() => setStatus(r, "revoke")} className="text-emerror/90 text-sm hover:text-emerror" data-testid={`revoke-${r.email}`}>Revoke</button>
              ) : (
                <button onClick={() => setStatus(r, "activate")} className="text-gold text-sm hover:underline" data-testid={`activate-${r.email}`}>Re-activate</button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
